<template>
  <content-section
    icon="luggage"
    :title="
      has_bags ? `Puoi aggiungere bagagli in stiva` : `Bagagli in stiva extra`
    "
    :description="extra_bags_description"
    classes="additional-bags-panel"
  >
    <template v-if="has_bags" class="mt-2 px-1">
      <additional-traveler-bags
        v-for="(traveler, traveler_index) in carrying_travelers"
        :key="`extra-bags:${traveler.id}`"
        :traveler="traveler"
        :traveler_index="traveler_index"
        :selected="selected_bags[traveler.id]"
        :flight_offer="flight_offer"
        :flexible_bags="flexible_bags"
        :bags="bags"
        :select_bag="select_bag"
        :itinerary_contains_bag="itinerary_contains_bag"
      />
      <b-row>
        <b-col>
          <div class="text-right px-3">
            <vb-button
              :vb_options="{
                variant: 'info',
                size: 'md',
                disabled: !has_pending_bag_changes,
              }"
              @click="
                handle_add_flexible_bag_to_flight_offer(
                  flight_offer,
                  flexible_bags
                )
              "
            >
              Aggiorna il costo del volo
            </vb-button>
          </div>
        </b-col>
      </b-row>
    </template>
  </content-section>
</template>

<script>
  import { mapState } from 'vuex';
  import { number } from 'mathjs';
  import AdditionalTravelerBags from '@liveflight/components/blocks/additional-bags/additional-traveler-bags.vue';
  import ContentSection from '@liveflight/components/blocks/wrappers/content-section';
  import {
    get_trip_type_from_itineraries,
    TRIP_TYPES,
  } from '@liveflight/utilities/origin-destination';

  const UNSET_BAG_SELECTION = '-1';

  export default {
    name: 'additional-bags-panel',
    components: {
      'additional-traveler-bags': AdditionalTravelerBags,
      'content-section': ContentSection,
    },
    props: {
      traveler_details: Array,

      flexible_bags: Object,
      bags: Object,

      has_pending_bag_changes: Boolean,
      flight_offer: Object,

      handle_add_flexible_bag_to_flight_offer: Function,
      flexible_bags_updated: Function,
    },
    data() {
      return {
        selected_bags_state: JSON.stringify({}),
      };
    },
    computed: {
      ...mapState('liveflight', ['flight_offer_has_upsell']),
      has_bags() {
        return Object.keys(this.bags).length > 0;
      },
      extra_bags_description() {
        if (!this.has_bags) {
          if (this.flight_offer_has_upsell) {
            return `Non è possibile aggiungere bagagli in stiva extra su questo volo. Normalmente è possibile farlo dopo la prenotazione sul sito della compagnia stessa ma la possibilità è soggetta a termini e condizioni definiti dalla compagnia. Puoi anche verificare in questa pagina se una delle tariffe proposte include il bagaglio in stiva.`;
          }
          return `Non è possibile aggiungere bagagli in stiva extra su questo volo. Normalmente è possibile farlo dopo la prenotazione sul sito della compagnia stessa ma la possibilità è soggetta a termini e condizioni definiti dalla compagnia.`;
        }
        const trip_type = get_trip_type_from_itineraries(this.itineraries);
        if (trip_type === TRIP_TYPES.ONE_WAY) {
          return '';
        }
        if (trip_type === TRIP_TYPES.ROUND_TRIP) {
          return `Il bagaglio verrà aggiunto su andata e ritorno.`;
        }
        return `Il bagaglio verrà aggiunto su tutte le tratte.`;
      },
      carrying_travelers() {
        const valid_travelers = Object.values(this.bags).flatMap(
          (bag) => bag.travelerIds
        );
        return this.traveler_details.filter((traveler) =>
          valid_travelers.includes(traveler.id)
        );
      },
      itineraries() {
        return Object.entries(this.flight_offer.itineraries || {}).map(
          (itinerary) => ({
            itinerary_id: itinerary[0],
            ...itinerary[1],
          })
        );
      },
      selected_bags: {
        get() {
          try {
            const parsed = JSON.parse(this.selected_bags_state);
            return parsed;
          } catch (e) {
            this.log('selected_bags error');
            this.log(e);
            return {};
          }
        },
        set(value) {
          this.selected_bags_state = JSON.stringify(value);
        },
      },
    },
    watch: {
      carrying_travelers: {
        handler(newValue) {
          this.initialize_selections(newValue);
        },
      },
    },
    mounted() {
      this.initialize_selections(this.carrying_travelers);
    },
    methods: {
      initialize_selections(carrying_travelers) {
        // Empty selections
        const selected_state = {};

        // Selections
        for (let index = 0; index < carrying_travelers.length; index += 1) {
          const traveler = carrying_travelers[index];
          selected_state[traveler.id] =
            this.get_selected_bags(traveler.id) || UNSET_BAG_SELECTION;
        }

        this.selected_bags = selected_state;
      },
      itinerary_contains_bag(bag, itinerary) {
        if (!bag || !itinerary) {
          return false;
        }
        const itinerary_segments = (itinerary.segments || []).map(
          (segment) => segment.id
        );
        return itinerary_segments.every((segment_id) =>
          bag.segmentIds.includes(segment_id)
        );
      },
      select_bags_from_id(traveler_index, bag_ids, selected) {
        const bags = bag_ids.split(':');
        for (let index = 0; index < bags.length; index++) {
          const bag_id = bags[index];
          if (bag_id !== UNSET_BAG_SELECTION) {
            const bag = this.bags[bag_id];
            if (!bag) {
              continue;
            }

            for (
              let itinerary_index = 0;
              itinerary_index < this.itineraries.length;
              itinerary_index += 1
            ) {
              const itinerary = this.itineraries[itinerary_index];
              if (!this.itinerary_contains_bag(bag, itinerary)) {
                continue;
              }

              this.flexible_bags_updated(selected, {
                traveler_index,
                itinerary_index: number(itinerary.itinerary_id),
                bag_index: bag_id,
                skip_options_cleanup: true,
              });
            }
          }
        }
      },
      select_bag(traveler_id, bag_ids) {
        const previous_selection = this.selected_bags[traveler_id];
        if (previous_selection === bag_ids) {
          return;
        }
        const traveler_index = this.traveler_details.findIndex(
          (t) => t.id === traveler_id
        );
        if (traveler_index < 0) {
          return;
        }
        this.select_bags_from_id(traveler_index, previous_selection, false);
        this.selected_bags = {
          ...this.selected_bags,
          [traveler_id]: bag_ids,
        };
        this.select_bags_from_id(traveler_index, bag_ids, true);
      },
      get_selected_bags(traveler_id) {
        const bag_ids = [];
        const traveler_index = this.traveler_details.findIndex(
          (t) => t.id === traveler_id
        );
        if (traveler_index < 0) {
          return;
        }
        const traveler_bags = Object.values(this.flexible_bags[traveler_index]);
        for (
          let itinerary_index = 0;
          itinerary_index < traveler_bags.length;
          itinerary_index += 1
        ) {
          const entries = Object.entries(traveler_bags[itinerary_index]);
          for (let index = 0; index < entries.length; index += 1) {
            const entry = entries[index];
            const bag_id = entry[0];
            const bag_info = entry[1];

            if (bag_info.added && !bag_ids.includes(String(bag_id))) {
              bag_ids.push(bag_id);
            }
          }
        }

        bag_ids.sort((a, b) => number(a) - number(b));
        return bag_ids.join(':');
      },
    },
  };
</script>
